// File: frontend/src/components/STIX/IntegrationGuidesSidebar.tsx
import React from 'react';
import {
  Paper,
  Typography,
  Box,
  Divider,
  alpha,
  styled
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import SchoolIcon from '@mui/icons-material/School';
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';

const SidebarPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  background: alpha(theme.palette.background.paper, 0.9),
  backdropFilter: 'blur(10px)',
  borderRadius: theme.spacing(2),
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  position: 'sticky',
  top: theme.spacing(2),
}));

const InfoBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.spacing(1),
  background: alpha(theme.palette.info.main, 0.1),
  border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
  marginBottom: theme.spacing(2),
}));

const ResearchBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.spacing(1),
  background: alpha(theme.palette.primary.main, 0.05),
  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
}));

export const IntegrationGuidesSidebar: React.FC = () => {
  return (
    <SidebarPaper elevation={2}>
      <Box display="flex" alignItems="center" mb={2}>
        <IntegrationInstructionsIcon sx={{ fontSize: 28, mr: 1, color: 'primary.main' }} />
        <Typography variant="h6" fontWeight="bold">
          STIX 2.1 Integration
        </Typography>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* Compatibility Info */}
      <InfoBox>
        <Box display="flex" alignItems="flex-start" mb={1}>
          <InfoIcon sx={{ fontSize: 20, mr: 1, color: 'info.main', mt: 0.2 }} />
          <Typography variant="body2" fontWeight="600" color="info.main">
            Platform Compatibility
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary">
          STIX 2.1 bundles are compatible with most modern threat intelligence platforms,
          including MISP, OpenCTI, Microsoft Sentinel, and other STIX-compliant systems.
        </Typography>
      </InfoBox>

      {/* Integration Guides Coming Soon */}
      <Box mb={3}>
        <Typography variant="body2" fontWeight="600" gutterBottom>
          📚 Integration Guides
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          We are actively developing comprehensive integration guides for leading threat intelligence platforms.
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
          Detailed documentation for MISP, OpenCTI, Sentinel, and other platforms will be available soon.
        </Typography>
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Research Note */}
      <ResearchBox>
        <Box display="flex" alignItems="flex-start" mb={1}>
          <SchoolIcon sx={{ fontSize: 20, mr: 1, color: 'primary.main', mt: 0.2 }} />
          <Typography variant="body2" fontWeight="600" color="primary.main">
            Academic Research
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
          The LLM-powered STIX 2.1 generation system is the result of an academic collaboration
          and forms the foundation of TI Mindmap HUB's research project. This innovative approach
          combines artificial intelligence with structured threat intelligence standards to automate
          the extraction and standardization of cyber threat data.
        </Typography>
      </ResearchBox>

      <Divider sx={{ my: 2 }} />

      {/* Quick Tip */}
      <Typography variant="caption" color="text.secondary" display="block" sx={{ textAlign: 'center' }}>
        💡 Download bundles and import them into your TIP using standard STIX 2.1 import functionality
      </Typography>
    </SidebarPaper>
  );
};

export default IntegrationGuidesSidebar;
